<template>
  <a-modal
    :visible="visible"
    :destroy-on-close="true"
    title="Add Work Experience to Role"
    ok-text="Save"
    :ok-button-props="{
      disabled: saveDisabled || isLoading,
      loading: isLoading,
    }"
    @ok="handleSave"
    @cancel="closeModal"
  >
    <a-tabs :active-key="activeTab" @change="handleTabChange">
      <!-- ADD EXISTING TAB -->
      <a-tab-pane key="0">
        <template #tab>
          <span>Add Existing</span>
        </template>

        <a-form layout="horizontal">
          <a-form-item :label-col="{ span: 8 }" :wrapper-col="{ span: 16 }" label="Work Experience">
            <a-select
              v-model="selectedWorkExperienceId"
              :loading="loadingWorkExperiences"
              show-search
              placeholder="Select a work experience"
              option-filter-prop="children"
              @change="() => (activeTab = '0')"
            >
              <a-select-option
                v-for="workExperience in availableWorkExperiences"
                :key="workExperience.id"
              >
                {{ workExperience.title }} at {{ workExperience.company_name }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-form>
      </a-tab-pane>

      <!-- CREATE NEW TAB -->
      <a-tab-pane key="1">
        <template #tab>
          <span>Create New</span>
        </template>

        <a-form layout="horizontal" data-vv-scope="create-work-experience">
          <a-alert
            v-if="showFutureRoleStartDateWarning"
            class="mb-3"
            type="warning"
            message="Role start date is in the future and was not prefilled."
            show-icon
          />

          <a-form-item
            :label-col="{ span: 8 }"
            :wrapper-col="{ span: 16 }"
            :validate-status="
              $validator.errors.first('create-work-experience.company_name') ? 'error' : ''
            "
            :help="$validator.errors.first('create-work-experience.company_name')"
            label="Company"
          >
            <a-input
              v-model="newWorkExperience.company_name"
              v-validate="'required|max:255'"
              data-vv-name="company_name"
              data-vv-as="company name"
              placeholder="Name of the company"
              @focus="() => (activeTab = '1')"
            />
          </a-form-item>

          <a-form-item
            :label-col="{ span: 8 }"
            :wrapper-col="{ span: 16 }"
            :validate-status="
              $validator.errors.first('create-work-experience.title') ? 'error' : ''
            "
            :help="$validator.errors.first('create-work-experience.title')"
            label="Title"
          >
            <a-input
              v-model="newWorkExperience.title"
              v-validate="'required|max:255'"
              placeholder="Title of the job"
              data-vv-name="title"
            />
          </a-form-item>

          <a-form-item
            :label-col="{ span: 8 }"
            :wrapper-col="{ span: 16 }"
            :validate-status="
              $validator.errors.first('create-work-experience.description') ? 'error' : ''
            "
            :help="$validator.errors.first('create-work-experience.description')"
            label="Description"
          >
            <a-textarea
              v-model="newWorkExperience.description"
              v-validate="'required'"
              placeholder="Description of the job"
              data-vv-name="description"
            />
            <a
              v-if="disableDescriptionBot && props.profile?.id === getTargetUserId()"
              class="d-flex mb-3"
              :href="getDescriptionBotLink()"
              target="_blank"
              >Get help writing a description</a
            >
          </a-form-item>

          <a-form-item :label-col="{ span: 8 }" :wrapper-col="{ span: 16 }" label="Skills">
            <a-select
              v-model="newWorkExperience.skill_ids"
              mode="multiple"
              show-search
              placeholder="Select skills"
              option-filter-prop="children"
            >
              <a-select-option v-for="s in availableSkills" :key="s.id" :value="s.id">
                {{ s.title }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item
            :label-col="{ span: 8 }"
            :wrapper-col="{ span: 16 }"
            :validate-status="
              $validator.errors.first('create-work-experience.start_date') ? 'error' : ''
            "
            :help="$validator.errors.first('create-work-experience.start_date')"
            label="Start date"
          >
            <a-date-picker
              v-validate="'required'"
              :value="parseDate(newWorkExperience.start_date)"
              :disabled-date="disableStartDate"
              data-vv-name="start_date"
              data-vv-as="start date"
              class="w-100"
              @change="(d) => onDateChange(d, 'start_date')"
            />
          </a-form-item>

          <a-form-item
            :label-col="{ span: 8 }"
            :wrapper-col="{ span: 16 }"
            :validate-status="
              $validator.errors.first('create-work-experience.end_date') ? 'error' : ''
            "
            :help="$validator.errors.first('create-work-experience.end_date')"
            label="End date"
          >
            <a-date-picker
              :value="parseDate(newWorkExperience.end_date)"
              :disabled-date="disableEndDate"
              data-vv-name="end_date"
              data-vv-as="end date"
              class="w-100"
              @change="(d) => onDateChange(d, 'end_date')"
            />
          </a-form-item>
        </a-form>
      </a-tab-pane>
    </a-tabs>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, ref, watch, toRef, getCurrentInstance } from "vue";
import moment from "@/date";
import { WorkExperienceInterface } from "@/modules/users/types";
import { SkillInterface } from "@/modules/skills/types";
import { RoleInterface } from "@/modules/roles/types";
import { apiStoreWorkExperience } from "../_utils/api";
import { apiUpdateRole } from "@/modules/roles/_utils/api";
import { apiGetUser } from "@/modules/users/_utils/api";
import useFilters from "@/useFilters";
const disableDescriptionBot = import.meta.env.VITE_DISABLE_DESCRIPTION_BOT;

// Props
const props = defineProps({
  visible: { type: Boolean, default: false },
  profile: { type: Object as () => any, default: undefined },
  skills: { type: Array as () => Array<SkillInterface>, default: () => [] },
  role: { type: Object as () => RoleInterface, default: undefined },
  roleId: { type: Number, default: undefined },
  userId: { type: Number, default: undefined },
});

// Emits
const emits = defineEmits(["work-experience-added", "close"]);

// Instances
const instance = getCurrentInstance();
const $validator = instance?.proxy.$validator;
const $message = instance?.proxy.$message;

// Filters
const { parseDate } = useFilters();

// Data properties
const selectedWorkExperienceId = ref<number | null>(null);
const availableWorkExperiences = ref<Array<WorkExperienceInterface>>([]);
const activeTab = ref<string>("0");
const isLoading = ref<boolean>(false);
const loadingWorkExperiences = ref<boolean>(false);
const newWorkExperience = ref<any>({
  company_name: null,
  title: null,
  description: null,
  skill_ids: [],
  start_date: null,
  end_date: null,
});

const attachedWorkExperienceIds = (): Array<number> => {
  return (props.role?.work_experiences || []).map(
    (workExperience: WorkExperienceInterface) => workExperience.id
  );
};

const saveDisabled = computed((): boolean => {
  if (activeTab.value === "0") {
    return !selectedWorkExperienceId.value;
  }

  return !newWorkExperience.value.title;
});

const showFutureRoleStartDateWarning = computed((): boolean => {
  if (!props.role?.start_date) {
    return false;
  }

  return moment(props.role.start_date, "YYYY-MM-DD").isAfter(moment().startOf("day"), "day");
});

const availableSkills = computed((): Array<SkillInterface> => {
  const skillMap = new Map<number, SkillInterface>();

  props.skills.forEach((skill: SkillInterface) => {
    skillMap.set(skill.id, skill);
  });

  (props.role?.skills || []).forEach((skill: SkillInterface) => {
    if (!skillMap.has(skill.id)) {
      skillMap.set(skill.id, skill);
    }
  });

  return Array.from(skillMap.values());
});

// Watchers
watch(toRef(props, "visible"), (newProp, oldProp) => {
  if (newProp && !oldProp) {
    isLoading.value = false;
    selectedWorkExperienceId.value = null;
    activeTab.value = "0";

    const roleStartDate = props.role?.start_date
      ? moment(props.role.start_date, "YYYY-MM-DD")
      : null;
    const roleEndDate = props.role?.end_date ? moment(props.role.end_date, "YYYY-MM-DD") : null;

    const roleDates = {
      start_date:
        roleStartDate && !roleStartDate.isAfter(moment().startOf("day"), "day")
          ? props.role?.start_date
          : null,
      end_date:
        roleEndDate && !roleEndDate.isAfter(moment().startOf("day"), "day")
          ? props.role?.end_date
          : null,
    };

    newWorkExperience.value = {
      company_name: props.role?.project?.client?.name || null,
      title: props.role?.title || null,
      description: props.role?.description || null,
      skill_ids: props.role?.skills?.map((s: any) => s.id) || [],
      ...roleDates,
    };

    // Load available work experiences for the user
    loadWorkExperiences();
  }
});

watch(activeTab, (newTab) => {
  if (newTab === "0") {
    loadWorkExperiences();
  }
});

// Methods
const loadWorkExperiences = (): void => {
  if (!props.role?.user?.id) {
    availableWorkExperiences.value = [];
    return;
  }

  loadingWorkExperiences.value = true;
  apiGetUser(props.role.user.id)
    .then((res: any) => {
      const workExperiences = (res.data.data.work_experiences ||
        []) as Array<WorkExperienceInterface>;
      const existingIds = new Set(attachedWorkExperienceIds());
      availableWorkExperiences.value = workExperiences.filter((we: any) => !existingIds.has(we.id));

      // Default to "Create New" tab if no work experiences are available
      if (availableWorkExperiences.value.length === 0) {
        activeTab.value = "1";
      }
    })
    .catch(() => {
      $message?.error("Couldn't load work experiences!", 3);
    })
    .finally(() => {
      loadingWorkExperiences.value = false;
    });
};

const handleTabChange = (tabKey: string): void => {
  activeTab.value = tabKey;
};

const disableStartDate = (current: any): any => {
  const today = moment().startOf("day");
  return current && moment(current).isAfter(today);
};

const disableEndDate = (current: any): any => {
  const today = moment().endOf("day");
  const isBeforeStartDate =
    current &&
    newWorkExperience.value.start_date &&
    current < moment(newWorkExperience.value.start_date).startOf("day");
  const isInFuture = current && moment(current).isAfter(today);
  return isBeforeStartDate || isInFuture;
};

const onDateChange = (date: any, attribute: string): void => {
  if (date === null) {
    newWorkExperience.value[attribute] = null;
  } else {
    newWorkExperience.value[attribute] = date.format("YYYY-MM-DD");
  }
};

const handleSave = (): void => {
  isLoading.value = true;

  if (activeTab.value === "0") {
    if (selectedWorkExperienceId.value && props.role) {
      const updatedWorkExperienceIds = [
        ...attachedWorkExperienceIds(),
        selectedWorkExperienceId.value,
      ];

      apiUpdateRole(props.role.project.id, props.role.id, {
        work_experience_ids: updatedWorkExperienceIds,
      })
        .then(() => {
          $message?.success("Work experience added!", 3);
          emits("work-experience-added", {
            type: "existing",
            workExperienceId: selectedWorkExperienceId.value,
          });
        })
        .catch(() => {
          $message?.error("Couldn't add work experience to role!", 3);
        })
        .finally(() => {
          isLoading.value = false;
        });
    } else {
      isLoading.value = false;
    }
  } else {
    $validator?.validateAll("create-work-experience").then((result: any) => {
      if (result) {
        const targetUserId = getTargetUserId();

        if (!targetUserId) {
          $message?.error("Couldn't determine target user for work experience.", 3);
          isLoading.value = false;
          return;
        }

        apiStoreWorkExperience(newWorkExperience.value, targetUserId)
          .then((res: any) => {
            const newWorkExperienceId = res.data.data.id;
            const updatedWorkExperienceIds = [...attachedWorkExperienceIds(), newWorkExperienceId];

            return apiUpdateRole(props.role?.project.id, props.role?.id, {
              work_experience_ids: updatedWorkExperienceIds,
            }).then(() => newWorkExperienceId);
          })
          .then((workExperienceId) => {
            $message?.success("Work experience created and added!", 3);
            emits("work-experience-added", {
              type: "new",
              workExperienceId,
            });
          })
          .catch(() => {
            $message?.error("Couldn't create work experience!", 3);
          })
          .finally(() => {
            isLoading.value = false;
          });
      } else {
        isLoading.value = false;
      }
    });
  }
};

const closeModal = (): void => {
  emits("close");
};

const getTargetUserId = (): number | undefined => {
  return props.userId || props.role?.user?.id || props.profile?.id;
};

const getDescriptionBotLink = (): string => {
  return `https://role-describer.ai.skry.se/?user_id=${props.profile?.id}&work_experience_id=NULL&embed=true`;
};
</script>

<style scoped>
.w-100 {
  width: 100%;
}
</style>
